import { useEffect, useRef, useState } from "react";
import { isMealTransferCommand } from "../../../backend/src/rules/meal-transfer.js";
import { MealCommandCancelled } from "../../application/duplicate-food";
import { detectMealType } from "../../application/meal-detection";
import { MealTransferError } from "../../application/meal-transfer";
import { clarifyVoiceMealRemotely, correctVoiceMealRemotely, parseVoiceMealRemotely, type MealClarification, type ParsedVoiceMealDraft } from "../../application/remote-meal-parser";
import { extractVoiceFinishCommand, isVoiceCancelCommand } from "../../application/voice-commands";
import { mergeVoiceTranscript } from "../../application/voice-transcript";
import type { VoiceTranscriptionSession } from "../../application/voice-transcription";
import type { MealType } from "../../domain/model";
import { useMealCorrection } from "./useMealCorrection";
import { useMealRecognition } from "./useMealRecognition";
import { useMealTransfer } from "./useMealTransfer";
import { useProcessingProgress } from "./useProcessingProgress";
import { voiceErrorText } from "./voice-errors";
import { useMealPersistence } from "../meal-editor/useMealPersistence";

type VoiceState = "closed" | "starting" | "listening" | "processing" | "meal-question" | "parsing" | "clarification-speaking" | "clarification-listening" | "error";
const mealParseSteps = ["Разбираю, что вы съели…", "Сверяю продукты…", "Считаю калории и БЖУ…", "Собираю результат…", "Ответ занимает чуть больше времени…"] as const;

export function useMealEntry({ selectedDate, repository, voiceProvider, onNavigate, onCloseDaySummary, onLoadError, previewDraft = null }: { selectedDate: string; repository: import("../../application/meal-repository").MealRepository; voiceProvider: import("../../application/voice-transcription").VoiceTranscriptionProvider; onNavigate: (date: string) => void; onCloseDaySummary: () => void; onLoadError: () => void; previewDraft?: ParsedVoiceMealDraft | null }) {
  const [voiceState, setVoiceState] = useState<VoiceState>("closed");
  const [voiceText, setVoiceText] = useState("");
  const [voiceError, setVoiceError] = useState("");
  const [debugDraft, setDebugDraft] = useState<ParsedVoiceMealDraft | null>(() => previewDraft);
  const [blockingPopupOpen, setBlockingPopupOpen] = useState(false);
  const persistence = useMealPersistence(repository, selectedDate);
  const mealSaveStatus = persistence.status;
  const [editingMealType, setEditingMealType] = useState<MealType | null>(() => previewDraft?.mealType ?? null);
  const [mealDetailsExpanded, setMealDetailsExpanded] = useState(false);
  const [emptyMealType, setEmptyMealType] = useState<MealType | null>(null);
  const [clarificationQuestion, setClarificationQuestion] = useState("");
  const voiceSession = useRef<VoiceTranscriptionSession | null>(null);
  const voiceFinalText = useRef("");
  const selectedMealRef = useRef<MealType | null>(null);
  const clarificationDraftRef = useRef<ParsedVoiceMealDraft | null>(null);
  const clarificationRef = useRef<MealClarification | null>(null);
  const clarificationAnswerRef = useRef("");
  const clarificationCountRef = useRef(0);
  const clarificationAttemptRef = useRef(0);
  const clarificationStartTimer = useRef<number | null>(null);
  const clarificationHardTimer = useRef<number | null>(null);
  const replaceMealTypeRef = useRef<MealType | null>(null);
  const returnMealTypeRef = useRef<MealType | null>(null);
  const mealParseProgress = useProcessingProgress(voiceState === "processing" || voiceState === "parsing", mealParseSteps);
  const mealRecognition = useMealRecognition(draft => presentParsedDraft(draft));
  const clearClarificationTimers = () => {
    if (clarificationStartTimer.current !== null) window.clearTimeout(clarificationStartTimer.current);
    if (clarificationHardTimer.current !== null) window.clearTimeout(clarificationHardTimer.current);
    clarificationStartTimer.current = null;
    clarificationHardTimer.current = null;
  };
  const closeVoiceInput = () => {
    voiceSession.current?.abort();
    voiceSession.current = null;
    clarificationAttemptRef.current += 1;
    clearClarificationTimers();
    window.speechSynthesis?.cancel();
    mealRecognition.cancel();
    setVoiceState("closed");
  };
  const finishClarification = async (answer: string) => {
    const draft = clarificationDraftRef.current;
    const clarification = clarificationRef.current;
    if (!draft || !clarification || !answer.trim()) return;
    setVoiceState("parsing");
    clarificationCountRef.current += 1;
    try {
      presentParsedDraft(await clarifyVoiceMealRemotely(draft, clarification, answer.trim()));
    } catch {
      setVoiceError("Не удалось обработать ответ. Попробуйте сказать его ещё раз.");
      setVoiceState("error");
    }
  };
  const submitTextMeal = (text: string) => {
    const answer = text.trim();
    if (!answer) return;
    voiceSession.current?.abort();
    voiceSession.current = null;
    window.speechSynthesis?.cancel();
    voiceFinalText.current = answer;
    setVoiceText(answer);
    setVoiceError("");
    if (clarificationDraftRef.current && clarificationRef.current && (voiceState.startsWith("clarification-") || voiceState === "error")) {
      void finishClarification(answer);
      return;
    }
    const meal = detectMealType(answer) ?? selectedMealRef.current;
    if (meal) {
      selectedMealRef.current = meal;
      showVoiceResult(meal);
    } else {
      setVoiceState("meal-question");
    }
  };
  const startClarificationListening = () => {
    clearClarificationTimers();
    window.speechSynthesis?.cancel();
    clarificationAnswerRef.current = "";
    setVoiceText("");
    setVoiceState("starting");
    voiceSession.current = voiceProvider.start("ru-RU", {
      onListening: () => setVoiceState("clarification-listening"),
      onCaptureEnd: () => setVoiceState("processing"),
      onInterim: text => {
        const combined = mergeVoiceTranscript(clarificationAnswerRef.current, text);
        setVoiceText(combined);
      },
      onFinal: text => {
        const combined = mergeVoiceTranscript(clarificationAnswerRef.current, text);
        clarificationAnswerRef.current = combined;
        setVoiceText(combined);
      },
      onError: error => {
        if (error === "aborted") return;
        setVoiceError(voiceErrorText[error]);
        setVoiceState("error");
      },
      onEnd: () => {
        const answer = clarificationAnswerRef.current;
        if (answer) void finishClarification(answer);
        else { setVoiceError(voiceErrorText["no-speech"]); setVoiceState("error"); }
      },
    });
  };

  const askClarification = (draft: ParsedVoiceMealDraft) => {
    const clarification = draft.clarifications.find(question => question.field !== "food") ?? draft.clarifications[0];
    if (!clarification) return;
    clarificationDraftRef.current = draft;
    clarificationRef.current = clarification;
    clarificationAnswerRef.current = "";
    setClarificationQuestion(clarification.question);
    setVoiceText("");
    setVoiceError("");
    setVoiceState("clarification-speaking");
    clearClarificationTimers();
    window.speechSynthesis?.cancel();
    const attempt = ++clarificationAttemptRef.current;
    let speechStarted = false;
    let microphoneStarted = false;
    const startMicrophoneOnce = () => {
      if (attempt !== clarificationAttemptRef.current || microphoneStarted) return;
      microphoneStarted = true;
      startClarificationListening();
    };
    if ("speechSynthesis" in window && "SpeechSynthesisUtterance" in window) {
      const utterance = new SpeechSynthesisUtterance(clarification.question);
      utterance.lang = "ru-RU";
      utterance.rate = 1;
      utterance.onstart = () => { speechStarted = true; };
      utterance.onend = startMicrophoneOnce;
      utterance.onerror = startMicrophoneOnce;
      window.speechSynthesis.speak(utterance);
      clarificationStartTimer.current = window.setTimeout(() => {
        if (!speechStarted) startMicrophoneOnce();
      }, 1_200);
      clarificationHardTimer.current = window.setTimeout(startMicrophoneOnce, Math.min(8_000, Math.max(3_000, clarification.question.length * 110)));
    } else {
      startMicrophoneOnce();
    }
  };

  const updateSavedDraft = (draft: ParsedVoiceMealDraft) => {
    setDebugDraft(draft);
    void persistence.persist(draft, persistence.isSaved() || editingMealType !== null);
  };
  const { transferMeal, notice: transferNotice } = useMealTransfer(repository, selectedDate, persistence.queue, date => {
    closeMealEditor(); closeVoiceInput(); setEmptyMealType(null); onCloseDaySummary();
    onNavigate(date);
  });
  const mealCorrection = useMealCorrection(voiceProvider, updateSavedDraft, (answer, draft, itemIndex) => {
    if (isMealTransferCommand(answer) && mealSaveStatus === "error") throw new MealTransferError("Сначала сохраните изменения текущего приёма пищи и повторите перенос.");
    return transferMeal(answer, draft.mealType, itemIndex);
  });

  const presentParsedDraft = (draft: ParsedVoiceMealDraft) => {
    clarificationDraftRef.current = draft;
    if (draft.clarifications.some(question => question.field !== "food") && clarificationCountRef.current < 3) {
      askClarification(draft);
      return;
    }
    persistence.resetStatus();
    if (returnMealTypeRef.current) setEditingMealType(returnMealTypeRef.current);
    setEmptyMealType(null);
    setDebugDraft(draft);
    onCloseDaySummary();
    closeVoiceInput();
    const replace = replaceMealTypeRef.current === draft.mealType;
    replaceMealTypeRef.current = null;
    void persistence.persist(draft, replace);
  };
  const confirmVoiceInput = async () => {
    const meal = selectedMealRef.current;
    if (!meal) return;
    voiceSession.current?.abort();
    voiceSession.current = null;
    setVoiceState("parsing");
    mealRecognition.begin();
    try {
      if (isMealTransferCommand(voiceFinalText.current)) {
        if (!returnMealTypeRef.current) throw new MealTransferError("Откройте исходный завтрак, обед или ужин и скажите команду переноса там.");
        if (await transferMeal(voiceFinalText.current, returnMealTypeRef.current)) return;
      }
      clarificationCountRef.current = 0;
      await persistence.queue.settled();
      const existing = await repository.getDraft(selectedDate, meal);
      replaceMealTypeRef.current = existing ? meal : null;
      const draft = existing ? await correctVoiceMealRemotely(existing, null, voiceFinalText.current) : await parseVoiceMealRemotely(voiceFinalText.current, meal);
      if (draft.clarifications.some(question => question.field !== "food") && clarificationCountRef.current < 3) presentParsedDraft(draft);
      else mealRecognition.show(draft);
    } catch (error) {
      if (error instanceof MealCommandCancelled) { replaceMealTypeRef.current = null; closeVoiceInput(); return; }
      mealRecognition.cancel();
      setVoiceError(error instanceof MealTransferError ? error.message : "Не удалось разобрать запись. Проверьте соединение и попробуйте ещё раз.");
      setVoiceState("error");
    }
  };
  useEffect(() => () => {
    clearClarificationTimers();
    clarificationAttemptRef.current += 1;
    window.speechSynthesis?.cancel();
    voiceSession.current?.abort();
  }, []);

  const closeMealEditor = () => {
    mealCorrection.cancel();
    setDebugDraft(null);
    setEditingMealType(null);
    setMealDetailsExpanded(false);
    returnMealTypeRef.current = null;
  };

  const saveCurrentMeal = async () => {
    if (!debugDraft?.nutrition || mealSaveStatus === "saving") return;
    if (persistence.isSaved()) {
      closeMealEditor();
      return;
    }
    if (await persistence.save(debugDraft, editingMealType !== null)) closeMealEditor();
  };

  const openSavedMeal = async (mealType: MealType) => {
    try {
      const draft = await repository.getDraft(selectedDate, mealType);
      selectedMealRef.current = mealType;
      returnMealTypeRef.current = mealType;
      persistence.resetStatus();
      setEditingMealType(mealType);
      setMealDetailsExpanded(false);
      persistence.markSaved(Boolean(draft));
      if (!draft) {
        setEmptyMealType(mealType);
        return;
      }
      setEmptyMealType(null);
      setDebugDraft(draft);
    } catch {
      onLoadError();
    }
  };

  const showVoiceResult = (meal: MealType) => {
    voiceSession.current?.abort();
    voiceSession.current = null;
    selectedMealRef.current = meal;
    void confirmVoiceInput();
  };

  const selectMeal = (meal: MealType) => {
    voiceSession.current?.abort();
    showVoiceResult(meal);
  };

  const askForMeal = () => {
    setVoiceState("meal-question");
    voiceSession.current = voiceProvider.start("ru-RU", {
      onListening: () => setVoiceState("meal-question"),
      onCaptureEnd: () => setVoiceState("processing"),
      onInterim: text => {
        const meal = detectMealType(text);
        if (meal) selectMeal(meal);
      },
      onFinal: text => {
        const meal = detectMealType(text);
        if (meal) selectMeal(meal);
      },
      onError: error => {
        if (error === "aborted") return;
        setVoiceError(voiceErrorText[error]);
        setVoiceState("error");
      },
      onEnd: () => setVoiceState("meal-question"),
    });
  };

  const finishFoodDictation = () => {
    if (isVoiceCancelCommand(voiceFinalText.current)) { voiceFinalText.current = ""; setVoiceText(""); setVoiceError(""); setVoiceState("closed"); return; }
    if (!voiceFinalText.current) {
      setVoiceText("");
      setVoiceError("");
      setVoiceState("closed");
      return;
    }
    const meal = detectMealType(voiceFinalText.current);
    if (meal) {
      showVoiceResult(meal);
      return;
    }
    if (selectedMealRef.current) {
      showVoiceResult(selectedMealRef.current);
      return;
    }
    askForMeal();
  };
  const startVoiceInput = (presetMeal: MealType | null = null) => {
    voiceSession.current?.abort();
    voiceFinalText.current = "";
    setVoiceText("");
    setVoiceError("");
    setClarificationQuestion("");
    clarificationDraftRef.current = null;
    clarificationRef.current = null;
    clarificationAnswerRef.current = "";
    clarificationCountRef.current = 0;
    selectedMealRef.current = presetMeal;
    returnMealTypeRef.current = presetMeal;
    persistence.markSaved(false);
    replaceMealTypeRef.current = null;
    setVoiceState("starting");
    voiceSession.current = voiceProvider.start("ru-RU", {
      onListening: () => setVoiceState("listening"),
      onCaptureEnd: () => setVoiceState("processing"),
      onInterim: text => {
        const combined = mergeVoiceTranscript(voiceFinalText.current, text);
        const finish = extractVoiceFinishCommand(combined);
        setVoiceText(finish.text);
        if (finish.finished) {
          voiceFinalText.current = finish.text;
          setVoiceState("processing");
          voiceSession.current?.stop();
        }
      },
      onFinal: text => {
        const combined = mergeVoiceTranscript(voiceFinalText.current, text);
        const finish = extractVoiceFinishCommand(combined);
        voiceFinalText.current = finish.text;
        setVoiceText(finish.text);
        if (finish.finished) {
          setVoiceState("processing");
          voiceSession.current?.stop();
        }
      },
      onError: error => {
        if (error === "aborted") return;
        setVoiceError(voiceErrorText[error]);
        setVoiceState("error");
      },
      onEnd: finishFoodDictation,
    });
  };

  const stopVoiceInput = () => {
    setVoiceState("processing");
    voiceSession.current?.stop();
  };

  const stopClarificationAnswer = () => {
    const answer = (clarificationAnswerRef.current || voiceText).trim();
    if (!answer) return;
    clarificationAnswerRef.current = answer;
    voiceSession.current?.stop();
  };

  const skipClarification = () => {
    voiceSession.current?.abort();
    window.speechSynthesis?.cancel();
    const draft = clarificationDraftRef.current;
    if (!draft) return;
    clarificationCountRef.current += 1;
    presentParsedDraft({ ...draft, clarifications: draft.clarifications.slice(1) });
  };

  const forceClarificationMicrophone = () => {
    clarificationAttemptRef.current += 1;
    clearClarificationTimers();
    window.speechSynthesis?.cancel();
    startClarificationListening();
  };

  const handleMealMicrophone = () => {
    if (voiceState === "starting" || voiceState === "listening") {
      stopVoiceInput();
      return;
    }
    if (voiceState === "clarification-listening") {
      stopClarificationAnswer();
      return;
    }
    if (voiceState !== "closed" && voiceState !== "error") return;
    if (debugDraft?.nutrition) {
      mealCorrection.toggle(debugDraft);
      return;
    }
    startVoiceInput();
  };

  const closeEmptyMealEditor = () => {
    closeVoiceInput();
    setEmptyMealType(null);
    setEditingMealType(null);
    returnMealTypeRef.current = null;
  };

  const handleEmptyMealMicrophone = () => {
    if (!emptyMealType) return;
    if (voiceState === "starting" || voiceState === "listening") {
      stopVoiceInput();
      return;
    }
    if (voiceState === "processing" || voiceState === "parsing") return;
    startVoiceInput(emptyMealType);
  };

  const hasClarification = clarificationDraftRef.current !== null;
  const leaveMealContext = () => { returnMealTypeRef.current = null; };

  return { voiceState, voiceText, voiceError, debugDraft, blockingPopupOpen, setBlockingPopupOpen, mealSaveStatus, editingMealType, mealDetailsExpanded, setMealDetailsExpanded, emptyMealType, clarificationQuestion, mealParseProgress, mealRecognition, mealCorrection, transferNotice, presentParsedDraft, closeVoiceInput, selectMeal, stopVoiceInput, skipClarification, forceClarificationMicrophone, stopClarificationAnswer, startVoiceInput, submitTextMeal, closeMealEditor, updateSavedDraft, saveCurrentMeal, closeEmptyMealEditor, handleEmptyMealMicrophone, openSavedMeal, handleMealMicrophone, hasClarification, leaveMealContext };
}
